"use client"

import { graphql, useQuery } from "@opensea/graphql"
import {
  CopyToClipboard,
  FlexColumn,
  formatAddress,
  Separator,
  SideSheetBody,
  SideSheetHeader,
  SideSheetHeaderTitle,
  TextBody,
  TextDisplay,
  UnstyledButton,
} from "@opensea/ui-kit"
import { ExpandMore } from "@opensea/ui-kit/icons"
import { QRCodeSVG } from "qrcode.react"
import type { OperationContext } from "urql"
import { useTranslations } from "use-intl"
import { Button } from "../../../components/Button"
import { useAccount } from "../../../polychain/hooks"

type DepositFlowContentProps = {
  onBack: (() => void) | undefined
}

export const DepositFlowContent = ({ onBack }: DepositFlowContentProps) => {
  const t = useTranslations("wallet.DepositFlowContent")
  const { address: connectedAddress, chainArch } = useAccount()

  if (!connectedAddress) {
    return null
  }

  // Display network message based on chain architecture
  const networkMessage =
    chainArch === "SVM" ? t("receivingNote.svm") : t("receivingNote.evm")

  return (
    <>
      <SideSheetHeader className="gap-4">
        {onBack && (
          <UnstyledButton onClick={onBack}>
            <ExpandMore className="rotate-90" size={24} title={t("back")} />
          </UnstyledButton>
        )}
        <SideSheetHeaderTitle>{t("deposit")}</SideSheetHeaderTitle>
      </SideSheetHeader>
      <Separator />
      <SideSheetBody className="flex grow flex-col items-center justify-center">
        <FlexColumn className="items-center justify-between p-6">
          <FlexColumn className="items-center gap-10">
            <TextDisplay className="p-2 text-center" size="sm">
              {t("scanOrCopy")}
            </TextDisplay>

            <FlexColumn>
              {connectedAddress && (
                <FlexColumn className="items-center gap-6">
                  <QRCodeSVG
                    className="rounded-md border border-border-1 bg-white"
                    includeMargin
                    level="H"
                    size={200}
                    value={connectedAddress}
                  />
                  <FlexColumn className="items-center gap-2">
                    <WalletName address={connectedAddress} />
                  </FlexColumn>
                </FlexColumn>
              )}
            </FlexColumn>

            <FlexColumn className="w-full gap-4">
              <CopyToClipboard text={connectedAddress}>
                <Button
                  className="w-full bg-black text-white"
                  variant="secondary"
                >
                  {t("copyWalletAddress")}
                </Button>
              </CopyToClipboard>
              <TextBody
                className="text-center"
                color="text-secondary"
                size="sm"
              >
                {networkMessage}
                {/* <Button variant="link">Learn more</Button> */}
              </TextBody>
            </FlexColumn>
          </FlexColumn>
        </FlexColumn>
      </SideSheetBody>
    </>
  )
}

const query = graphql(`
  query DepositFlowContentQuery($identifier: String!) {
    profileByIdentifier(identifier: $identifier) {
      __typename
      ... on Profile {
        ensName
      }
    }
  }
`)

const CONTEXT: Partial<OperationContext> = {
  suspense: false,
}

function WalletName({ address }: { address: string }) {
  const t = useTranslations("wallet.DepositFlowContent")
  const [{ data }] = useQuery({
    query,
    variables: { identifier: address },
    context: CONTEXT,
  })

  const ensName =
    data?.profileByIdentifier?.__typename === "Profile" &&
    data.profileByIdentifier.ensName
      ? data.profileByIdentifier.ensName
      : undefined

  return (
    <>
      <CopyToClipboard text={ensName || formatAddress(address)}>
        <TextDisplay className="font-mono" size="sm">
          {ensName || formatAddress(address)}
        </TextDisplay>
      </CopyToClipboard>
      {ensName && (
        <CopyToClipboard text={address} tooltipText={t("copyAddress")}>
          <TextBody className="font-mono" color="text-secondary" size="md">
            {formatAddress(address)}
          </TextBody>
        </CopyToClipboard>
      )}
    </>
  )
}
